import { m } from "@/paraglide/messages.js";

export type EglyAchievement = {
  title: string;
  detail?: string;
  /** Maintainer citation; not rendered on the public site. */
  source?: string;
  verified: boolean;
};

export type EglyHonorGroup = {
  heading: string;
  items: EglyAchievement[];
};

export type EglyNotableStudent = {
  name: string;
  achievement: string;
};

type AchievementMeta = {
  verified: boolean;
  source?: string;
  hasDetail: boolean;
};

const COMPETITIVE_META: AchievementMeta[] = [
  {
    verified: true,
    source: "https://iowagolf.org/hof-egly/",
    hasDetail: true,
  },
  {
    verified: true,
    source: "https://iowapga.com/iowa-openpastchampions/",
    hasDetail: true,
  },
  {
    verified: false,
    hasDetail: true,
  },
  {
    verified: true,
    source: "https://iowagolf.org/hof-egly/",
    hasDetail: true,
  },
  {
    verified: true,
    source: "https://www.pga.com/story/a-lesson-in-resilience-how-mark-egly-pga-beat-the-odds",
    hasDetail: true,
  },
  {
    verified: true,
    source: "https://golfrange.org/mark-egly-pga-a-retrospective-on-a-pros-pro-and-mentor-to-many/",
    hasDetail: true,
  },
];

const HONOR_META: { items: AchievementMeta[] }[] = [
  {
    items: [
      {
        verified: true,
        source: "https://iowapga.com/past-winners/",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://iowapga.com/past-winners/",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://iowapga.com/teacher-of-the-year-mark-atchis/",
        hasDetail: true,
      },
      {
        verified: true,
        source:
          "https://golfrange.org/mark-egly-pga-a-retrospective-on-a-pros-pro-and-mentor-to-many/",
        hasDetail: false,
      },
    ],
  },
  {
    items: [
      {
        verified: true,
        source: "https://www.pga.com/story/a-lesson-in-resilience-how-mark-egly-pga-beat-the-odds",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://iowagolf.org/hof-egly/",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://golfrange.org/winners/mark-egly-pga-8/",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://linkedin.com/in/markegly",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://golfrange.org/winners/mark-egly-5/",
        hasDetail: false,
      },
    ],
  },
  {
    items: [
      {
        verified: true,
        source:
          "https://golfrange.org/mark-egly-pga-a-retrospective-on-a-pros-pro-and-mentor-to-many/",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://iowapga.com/mark-egly-award-pga-of-america-national-deacon-palmer-award/",
        hasDetail: true,
      },
      {
        verified: true,
        source: "https://iowapga.com/mark-egly-award-pga-of-america-national-deacon-palmer-award/",
        hasDetail: false,
      },
    ],
  },
];

/** Verified only; pending items stay in catalogs for future editors. */
function verifiedAchievements(items: EglyAchievement[]): EglyAchievement[] {
  return items.filter((item) => item.verified);
}

export function getMarkEglyBioParagraphs(): string[] {
  return [
    m.about_content_bio_0(),
    m.about_content_bio_1(),
    m.about_content_bio_2(),
    m.about_content_bio_3(),
  ];
}

export function getMarkEglyResilience(): string {
  return m.about_content_resilience();
}

export function getMarkEglyGrowthIntro(): string {
  return m.about_content_growth_intro();
}

export function getMarkEglyGrowthBullets(): string[] {
  return [
    m.about_content_growth_bullet_0(),
    m.about_content_growth_bullet_1(),
    m.about_content_growth_bullet_2(),
    m.about_content_growth_bullet_3(),
    m.about_content_growth_bullet_4(),
  ];
}

export function getMarkEglyCompetitiveAchievements(): EglyAchievement[] {
  return verifiedAchievements([
    {
      title: m.about_content_competitive_0_title(),
      detail: m.about_content_competitive_0_detail(),
      source: COMPETITIVE_META[0]!.source,
      verified: COMPETITIVE_META[0]!.verified,
    },
    {
      title: m.about_content_competitive_1_title(),
      detail: m.about_content_competitive_1_detail(),
      source: COMPETITIVE_META[1]!.source,
      verified: COMPETITIVE_META[1]!.verified,
    },
    {
      title: m.about_content_competitive_2_title(),
      detail: m.about_content_competitive_2_detail(),
      source: COMPETITIVE_META[2]!.source,
      verified: COMPETITIVE_META[2]!.verified,
    },
    {
      title: m.about_content_competitive_3_title(),
      detail: m.about_content_competitive_3_detail(),
      source: COMPETITIVE_META[3]!.source,
      verified: COMPETITIVE_META[3]!.verified,
    },
    {
      title: m.about_content_competitive_4_title(),
      detail: m.about_content_competitive_4_detail(),
      source: COMPETITIVE_META[4]!.source,
      verified: COMPETITIVE_META[4]!.verified,
    },
    {
      title: m.about_content_competitive_5_title(),
      detail: m.about_content_competitive_5_detail(),
      source: COMPETITIVE_META[5]!.source,
      verified: COMPETITIVE_META[5]!.verified,
    },
  ]);
}

export function getMarkEglyTeachingHonorGroups(): EglyHonorGroup[] {
  return [
    {
      heading: m.about_content_honor_0_heading(),
      items: [
        {
          title: m.about_content_honor_0_item_0_title(),
          detail: m.about_content_honor_0_item_0_detail(),
          source: HONOR_META[0]!.items[0]!.source,
          verified: HONOR_META[0]!.items[0]!.verified,
        },
        {
          title: m.about_content_honor_0_item_1_title(),
          detail: m.about_content_honor_0_item_1_detail(),
          source: HONOR_META[0]!.items[1]!.source,
          verified: HONOR_META[0]!.items[1]!.verified,
        },
        {
          title: m.about_content_honor_0_item_2_title(),
          detail: m.about_content_honor_0_item_2_detail(),
          source: HONOR_META[0]!.items[2]!.source,
          verified: HONOR_META[0]!.items[2]!.verified,
        },
        {
          title: m.about_content_honor_0_item_3_title(),
          source: HONOR_META[0]!.items[3]!.source,
          verified: HONOR_META[0]!.items[3]!.verified,
        },
      ],
    },
    {
      heading: m.about_content_honor_1_heading(),
      items: [
        {
          title: m.about_content_honor_1_item_0_title(),
          detail: m.about_content_honor_1_item_0_detail(),
          source: HONOR_META[1]!.items[0]!.source,
          verified: HONOR_META[1]!.items[0]!.verified,
        },
        {
          title: m.about_content_honor_1_item_1_title(),
          detail: m.about_content_honor_1_item_1_detail(),
          source: HONOR_META[1]!.items[1]!.source,
          verified: HONOR_META[1]!.items[1]!.verified,
        },
        {
          title: m.about_content_honor_1_item_2_title(),
          detail: m.about_content_honor_1_item_2_detail(),
          source: HONOR_META[1]!.items[2]!.source,
          verified: HONOR_META[1]!.items[2]!.verified,
        },
        {
          title: m.about_content_honor_1_item_3_title(),
          detail: m.about_content_honor_1_item_3_detail(),
          source: HONOR_META[1]!.items[3]!.source,
          verified: HONOR_META[1]!.items[3]!.verified,
        },
        {
          title: m.about_content_honor_1_item_4_title(),
          source: HONOR_META[1]!.items[4]!.source,
          verified: HONOR_META[1]!.items[4]!.verified,
        },
      ],
    },
    {
      heading: m.about_content_honor_2_heading(),
      items: [
        {
          title: m.about_content_honor_2_item_0_title(),
          detail: m.about_content_honor_2_item_0_detail(),
          source: HONOR_META[2]!.items[0]!.source,
          verified: HONOR_META[2]!.items[0]!.verified,
        },
        {
          title: m.about_content_honor_2_item_1_title(),
          detail: m.about_content_honor_2_item_1_detail(),
          source: HONOR_META[2]!.items[1]!.source,
          verified: HONOR_META[2]!.items[1]!.verified,
        },
        {
          title: m.about_content_honor_2_item_2_title(),
          source: HONOR_META[2]!.items[2]!.source,
          verified: HONOR_META[2]!.items[2]!.verified,
        },
      ],
    },
  ];
}

export function getMarkEglyCareerHighlights(): string[] {
  return [
    m.about_content_career_0(),
    m.about_content_career_1(),
    m.about_content_career_2(),
    m.about_content_career_3(),
  ];
}

export function getMarkEglyNotableStudents(): EglyNotableStudent[] {
  return [
    {
      name: m.about_content_student_0_name(),
      achievement: m.about_content_student_0_achievement(),
    },
    {
      name: m.about_content_student_1_name(),
      achievement: m.about_content_student_1_achievement(),
    },
    {
      name: m.about_content_student_2_name(),
      achievement: m.about_content_student_2_achievement(),
    },
  ];
}
